import React, {
  useEffect,
  useState,
} from "react";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import { useCart } from "../../components/CartContext";

import { fetchPriceSummary } from "../../actions/priceActions";

import "./css/CheckoutScreen.css";


function CheckoutScreen() {

  const {
    restaurantId,
    tableId,
  } = useParams();


  const navigate =
    useNavigate();


  const dispatch =
    useDispatch();


  /* =====================================================
     CART
  ===================================================== */

  const {
    cart,
    cartCount,
    cartTotal,
    increaseQuantity,
    decreaseQuantity,
    removeFromCart,
  } = useCart();


  /* =====================================================
     PRICE SUMMARY
  ===================================================== */

  const [
    priceSummary,
    setPriceSummary,
  ] = useState(null);


  const [
    priceLoading,
    setPriceLoading,
  ] = useState(false);


  const [
    priceError,
    setPriceError,
  ] = useState(null);


  /* =====================================================
     ORDER STATE
  ===================================================== */

  const {
    loading: orderLoading,
  } = useSelector(
    (state) =>
      state.order || {}
  );


  /* =====================================================
     FETCH SERVER PRICE SUMMARY
  ===================================================== */

  useEffect(() => {

    if (
      !restaurantId ||
      cart.length === 0
    ) {
      return;
    }


    const loadPriceSummary =
      async () => {

        try {

          setPriceLoading(true);

          setPriceError(null);


          const items =
            cart.map(
              (item) => ({
                menu_item_id:
                  item.id,

                quantity:
                  Number(
                    item.quantity
                  ),
              })
            );


          const result =
            await dispatch(
              fetchPriceSummary(
                restaurantId,
                items
              )
            );


          if (result) {

            setPriceSummary(
              result
            );

          }

        } catch (error) {

          setPriceError(
            error?.message ||
            "Unable to calculate order total."
          );

        } finally {

          setPriceLoading(false);

        }

      };


    loadPriceSummary();

  }, [
    dispatch,
    restaurantId,
    cart,
  ]);


  /* =====================================================
     EMPTY CART
  ===================================================== */

  if (cart.length === 0) {

    return (

      <div
        className="theme2-checkout-screen"
      >

        <div
          className="theme2-checkout-empty"
        >

          <span>
            CHECKOUT
          </span>


          <h1>
            YOUR CART IS EMPTY
          </h1>


          <p>
            Please add something from
            our menu before checking out.
          </p>


          <button
            type="button"
            onClick={() =>
              navigate(
                `/${restaurantId}/table/${tableId}`
              )
            }
          >
            VIEW MENU
          </button>

        </div>

      </div>

    );

  }


  /* =====================================================
     SERVER TOTALS
  ===================================================== */

  const serverData =
    priceSummary?.data ||
    priceSummary ||
    {};


  const subtotal =
    Number(
      serverData.subtotal ??
      cartTotal ??
      0
    );


  const tax =
    Number(
      serverData.tax ??
      serverData.tax_amount ??
      0
    );


  const serviceCharge =
    Number(
      serverData.service_charge ??
      serverData.service_charge_amount ??
      0
    );


  const total =
    Number(
      serverData.total ??
      serverData.total_amount ??
      (
        subtotal +
        tax +
        serviceCharge
      )
    );


  /* =====================================================
     PLACE ORDER
  ===================================================== */

  const handlePlaceOrder = () => {

    navigate(
      `/${restaurantId}/table/${tableId}/order`,
      {
        state: {
          subtotal,
          tax,
          serviceCharge,
          total,
        },
      }
    );

  };


  /* =====================================================
     BACK TO CART
  ===================================================== */

  const goBackToCart = () => {

    navigate(
      `/${restaurantId}/table/${tableId}/cart`
    );

  };


  /* =====================================================
     BACK TO MENU
  ===================================================== */

  const goBackToMenu = () => {

    navigate(
      `/${restaurantId}/table/${tableId}`
    );

  };


  /* =====================================================
     RENDER
  ===================================================== */

  return (

    <div
      className="theme2-checkout-screen"
    >

      <div
        className="theme2-checkout-container"
      >

        {/* =================================================
            HEADER
        ================================================= */}

        <div
          className="theme2-checkout-heading"
        >

          <span>
            YOUR ORDER
          </span>


          <h1>
            CHECKOUT
          </h1>


          <p>
            {cartCount}{" "}
            {cartCount === 1
              ? "ITEM"
              : "ITEMS"}
          </p>

        </div>


        {/* =================================================
            MAIN CHECKOUT GRID
        ================================================= */}

        <div
          className="theme2-checkout-grid"
        >

          {/* ===============================================
              LEFT SIDE
          =============================================== */}

          <div
            className="theme2-checkout-main"
          >

            {/* =============================================
                ORDER ITEMS
            ============================================= */}

            <section
              className="theme2-checkout-section"
            >

              <div
                className="theme2-checkout-section-heading"
              >

                <span>
                  01
                </span>


                <h2>
                  ORDER
                </h2>

              </div>


              <div
                className="theme2-checkout-items"
              >

                {cart.map(
                  (item) => (

                    <article
                      className="theme2-checkout-item"
                      key={item.id}
                    >

                      {/* IMAGE */}

                      <div
                        className="theme2-checkout-item-image"
                      >

                        {item.image ? (

                          <img
                            src={item.image}
                            alt={item.name}
                          />

                        ) : (

                          <div
                            className="theme2-checkout-item-no-image"
                          >
                            NO IMAGE
                          </div>

                        )}

                      </div>


                      {/* INFO */}

                      <div
                        className="theme2-checkout-item-info"
                      >

                        <h3>
                          {item.name}
                        </h3>


                        <p>
                          {item.description ||
                            "Freshly prepared from our kitchen."}
                        </p>


                        <span>
                          ${item.price} ×{" "}
                          {item.quantity}
                        </span>

                      </div>


                      {/* QUANTITY */}

                      <div
                        className="theme2-checkout-item-quantity"
                      >

                        <button
                          type="button"
                          onClick={() =>
                            decreaseQuantity(
                              item.id
                            )
                          }
                          aria-label={
                            `Decrease ${item.name}`
                          }
                        >
                          −
                        </button>


                        <span>
                          {item.quantity}
                        </span>


                        <button
                          type="button"
                          onClick={() =>
                            increaseQuantity(
                              item.id
                            )
                          }
                          aria-label={
                            `Increase ${item.name}`
                          }
                        >
                          +
                        </button>

                      </div>


                      {/* TOTAL */}

                      <div
                        className="theme2-checkout-item-total"
                      >

                        ₹
                        {(
                          Number(
                            item.price
                          ) *
                          Number(
                            item.quantity
                          )
                        ).toFixed(2)}

                      </div>


                      {/* REMOVE */}

                      <button
                        type="button"
                        className="theme2-checkout-item-remove"
                        onClick={() =>
                          removeFromCart(
                            item.id
                          )
                        }
                      >
                        REMOVE
                      </button>

                    </article>

                  )
                )}

              </div>


              {/* EDIT CART */}

              <button
                type="button"
                className="theme2-checkout-back-cart"
                onClick={
                  goBackToCart
                }
              >
                EDIT CART
              </button>

            </section>

          </div>


          {/* ===============================================
              RIGHT SIDE SUMMARY
          =============================================== */}

          <aside
            className="theme2-checkout-summary"
          >

            <div
              className="theme2-checkout-summary-inner"
            >

              <div
                className="theme2-checkout-summary-label"
              >
                ORDER SUMMARY
              </div>


              <h2>
                YOUR ORDER
              </h2>


              {/* ITEM COUNT */}

              <div
                className="theme2-checkout-summary-row"
              >

                <span>
                  ITEMS
                </span>


                <strong>
                  {cartCount}
                </strong>

              </div>


              {/* SUBTOTAL */}

              <div
                className="theme2-checkout-summary-row"
              >

                <span>
                  SUBTOTAL
                </span>


                <strong>
                  ₹
                  {subtotal.toFixed(2)}
                </strong>

              </div>


              {/* TAX */}

              {tax > 0 && (

                <div
                  className="theme2-checkout-summary-row"
                >

                  <span>
                    TAX
                  </span>


                  <strong>
                    ₹
                    {tax.toFixed(2)}
                  </strong>

                </div>

              )}


              {/* SERVICE CHARGE */}

              {serviceCharge > 0 && (

                <div
                  className="theme2-checkout-summary-row"
                >

                  <span>
                    SERVICE
                  </span>


                  <strong>
                    ₹
                    {serviceCharge.toFixed(2)}
                  </strong>

                </div>

              )}


              {/* TOTAL */}

              <div
                className="theme2-checkout-summary-total"
              >

                <span>
                  TOTAL
                </span>


                <strong>
                  ₹
                  {total.toFixed(2)}
                </strong>

              </div>


              {/* PRICE LOADING */}

              {priceLoading && (

                <p
                  className="theme2-checkout-price-loading"
                >
                  Calculating final total...
                </p>

              )}


              {/* PRICE ERROR */}

              {priceError && (

                <p
                  className="theme2-checkout-price-error"
                >
                  {priceError}
                </p>

              )}


              {/* PLACE ORDER */}

              <button
                type="button"
                className="theme2-place-order-button"
                onClick={
                  handlePlaceOrder
                }
                disabled={
                  priceLoading ||
                  orderLoading
                }
              >

                {orderLoading
                  ? "PROCESSING..."
                  : "PLACE ORDER"}

              </button>


              {/* BACK TO MENU */}

              <button
                type="button"
                className="theme2-checkout-menu-button"
                onClick={
                  goBackToMenu
                }
              >
                CONTINUE ORDERING
              </button>


              <p
                className="theme2-checkout-note"
              >
                Your final amount will be
                confirmed before payment.
              </p>

            </div>

          </aside>

        </div>

      </div>

    </div>

  );

}


export default CheckoutScreen;